/**
 * Service FAQs — used both as visible accordion content
 * and as FAQPage JSON-LD for Google "People Also Ask" rich snippets.
 */

export const SERVICE_FAQS = {
  "architectural-wraps": [
    {
      q: "What surfaces can be wrapped with architectural vinyl?",
      a: "Almost any clean, smooth surface — cabinets, walls, doors, elevators, columns, reception desks, millwork, glass, and metal. We use premium 3M Di-Noc and Avery films that bond to laminate, melamine, drywall, MDF, and most painted surfaces. We confirm suitability during the on-site assessment.",
    },
    {
      q: "How long does an architectural wrap last?",
      a: "Indoor installs typically last 10–12 years with normal commercial use. Outdoor wraps are warranted 5–7 years depending on sun exposure. We only use 3M and Avery films with manufacturer-backed durability ratings — no consumer-grade vinyl.",
    },
    {
      q: "Can wraps be removed without damaging the surface?",
      a: "Yes. Premium architectural vinyl is designed to be removable without leaving residue or damaging the underlying surface, when removed within the product's lifespan. This is exactly why wraps work so well for leased commercial spaces.",
    },
    {
      q: "How much do architectural wraps cost vs. renovating?",
      a: "Architectural wraps typically deliver 80% of the visual impact of a renovation at 20% of the cost — and finish in 1–3 days instead of weeks. Final pricing depends on surface area, complexity, and material. We provide free quotes with no obligation.",
    },
    {
      q: "How long does installation take?",
      a: "Most projects complete in 1–3 days. A single elevator wrap is typically a same-day install. A full retail interior or large office reception wall is usually 2–3 days. We work after hours and weekends to avoid disrupting your business.",
    },
  ],

  "signs": [
    {
      q: "Do you handle sign permits in Toronto and the GTA?",
      a: "Yes. We work with Toronto, Mississauga, Vaughan, Brampton, and Markham permit offices regularly and can manage the application end-to-end as part of your project. Most municipal sign permits take 2–4 weeks; we factor this into the project timeline.",
    },
    {
      q: "What's the typical lead time for a custom sign?",
      a: "Channel letter signs and dimensional signs typically take 2–3 weeks from design approval to install. ACM panel signs are usually 7–10 business days. Reception signs and acrylic signs are often ready in 5–7 business days. Rush options are available.",
    },
    {
      q: "ACM vs. acrylic vs. channel letter — which is right for my business?",
      a: "ACM (aluminum composite) is best for large outdoor panel signs and storefront facades. Acrylic dimensional signs are ideal for indoor reception walls and offices. Channel letters are the premium outdoor option for storefronts, with or without LED illumination. We help you choose during your free consultation.",
    },
    {
      q: "Do you offer LED-illuminated channel letters?",
      a: "Yes. We fabricate front-lit, back-lit (halo), and double-lit channel letters using energy-efficient LEDs with 5–10 year lifespans. All electrical components are CSA-certified for Ontario commercial installations.",
    },
    {
      q: "Do you install the signs you fabricate?",
      a: "Yes — every project includes professional installation by our certified team. We handle mounting, electrical hookup (for illuminated signs), and final inspection. We do not just drop-ship signs.",
    },
  ],

  "window-graphics": [
    {
      q: "Will frosted window film block natural light?",
      a: "No. Frosted privacy film diffuses light without blocking it — your office stays bright, but the view through the glass becomes obscured. It's the same effect as etched or sandblasted glass, at a fraction of the cost.",
    },
    {
      q: "Can window graphics and frosted film be removed later?",
      a: "Yes. Our films are designed for clean removal without damaging the glass or leaving residue, as long as removed within the product lifespan (7+ years indoor). This makes them ideal for leased offices and rotating seasonal storefronts.",
    },
    {
      q: "How long does window film last?",
      a: "Indoor frosted film typically lasts 7–10 years. Storefront window graphics last 5–7 years with normal outdoor exposure. UV protection film (which blocks fade-causing rays) typically lasts 10+ years and can extend the life of carpets and furniture inside.",
    },
    {
      q: "Can you design custom branded patterns or logos?",
      a: "Yes. We design custom logo etches, gradient privacy patterns, geometric brand motifs, and full-print storefront graphics. We provide a free visual mockup so you can see the finished window before committing.",
    },
    {
      q: "How does one-way vision film work?",
      a: "Perforated one-way vision film allows you to see out from the inside while displaying full-color graphics on the outside. It's commonly used on retail storefronts and corporate offices that want privacy without sacrificing daylight or curb appeal.",
    },
  ],

  "wall-graphics": [
    {
      q: "Will floor decals slip, peel, or damage flooring?",
      a: "No. Our commercial floor decals use a textured anti-slip laminate rated for high-traffic indoor use. They're rated for 6 months to 2 years depending on traffic and surface. Floors are not damaged on removal when used as directed.",
    },
    {
      q: "Will wall murals damage paint when removed?",
      a: "Properly removed within the product's lifespan, our wall films will not damage well-cured paint. We recommend wall surfaces be painted and fully cured (minimum 30 days) before installation. We always inspect surfaces beforehand.",
    },
    {
      q: "What's the largest wall mural you can print?",
      a: "We print and install wall graphics in sections, so there's effectively no size limit. We've done full-wall murals in excess of 25 feet wide with seamless matching. Larger surfaces are installed in panels with imperceptible joins.",
    },
    {
      q: "Do you prep walls before installation?",
      a: "Yes. Surface prep is part of every install — we clean, degrease, and inspect the wall. If we identify issues (loose paint, texture, damage) we'll flag them before installation so the finished result is flawless.",
    },
  ],

  "installation": [
    {
      q: "Do you install vinyl or signage purchased elsewhere?",
      a: "Yes — install-only services are available for vinyl wraps, wall graphics, window film, and signs purchased from other suppliers. We assess the material on-site to confirm it meets installation standards before scheduling.",
    },
    {
      q: "Do you remove old vinyl, signs, and graphics?",
      a: "Yes. We remove existing vinyl, wraps, decals, and signage as a standalone service or as part of a re-branding project. Removal includes adhesive residue cleanup and surface restoration so it's ready for the new install.",
    },
    {
      q: "Do you offer maintenance and repairs for existing installs?",
      a: "Yes. We service existing wraps, signs, and graphics — including LED replacements on channel letters, vinyl touch-ups, and full re-wrap maintenance. Annual maintenance packages are available for multi-location commercial clients.",
    },
    {
      q: "How quickly can you schedule a site visit?",
      a: "Most GTA site visits are scheduled within 2–3 business days. Toronto and Mississauga we often book within 24–48 hours. Urgent commercial requests can sometimes be accommodated same-day depending on team availability.",
    },
  ],
};
